import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Button,
  Box,
  TextField,
  Alert,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { changePassword } from '../api';
import { useThemeContext } from '../ThemeContext';
import { useAuth } from '../AuthContext';
import { useLang } from '../i18n';

function Dashboard() {
  const theme = useTheme();
  const { t } = useLang();
  const { mode, toggleTheme } = useThemeContext();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      navigate('/');
    }
  }, [navigate]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('account.passwordsMismatch'));
      return;
    }
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(t('account.passwordChanged'));
      setError('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(t('account.passwordError'));
      setSuccess('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ mb: 2 }}>{t('account.title')}</Typography>
      <Typography sx={{ mb: 4 }}>{t('account.welcome')}</Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        <Button
          onClick={handleLogout}
          variant="outlined"
          sx={{
            color: theme.palette.text.primary,
            borderColor: theme.palette.text.primary,
            '&:hover': {
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              backgroundColor: 'transparent',
            },
          }}
        >
          {t('account.logout')}
        </Button>
        <Button
          component={Link}
          to="/voice-settings"
          variant="outlined"
          sx={{
            color: theme.palette.text.primary,
            borderColor: theme.palette.text.primary,
            '&:hover': {
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              backgroundColor: 'transparent',
            },
          }}
        >
          {t('account.voice')}
        </Button>
        <Button
          component={Link}
          to="/donate"
          variant="outlined"
          sx={{
            color: theme.palette.text.primary,
            borderColor: theme.palette.text.primary,
            '&:hover': {
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          {t('account.support')}
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{t('account.theme')}</Typography>
        <FormControlLabel
          control={<Switch checked={mode === 'dark'} onChange={toggleTheme} />}
          label={mode === 'dark' ? t('account.dark') : t('account.light')}
        />
      </Paper>

      <Typography variant="h5" sx={{ mb: 2 }}>{t('account.changePassword')}</Typography>
      <form onSubmit={handleChangePassword}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('account.currentPassword')}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
          <TextField
            label={t('account.newPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <TextField
            label={t('account.confirmNewPassword')}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          <Button type="submit" variant="contained">{t('account.changePassword')}</Button>
        </Box>
      </form>
    </Container>
  );
}

export default Dashboard;
